const sectionSelector = '[data-social-proof-animated]';
const cardSelector = '[data-social-proof-animated-card]';
const placeholdersSelector = '.position-placeholders';
const fadeDuration = 200;
const fadeStagger = 200;
const moveDuration = 850;
const moveStagger = 50;
const movePause = 100;
const moveEase = 'cubic-bezier(0.22, 1, 0.36, 1)';
const lockAtStartPosition = false;
const staticQuery = '(prefers-reduced-motion: reduce), (max-width: 1023.98px)';
// How far (ms) into the placement movement the text begins fading in.
const textRevealOffset = 150;

const setCardStartPoints = (section) => {
    return [...section.querySelectorAll(cardSelector)];
};

const animateCards = (cards) => {
    const fadedCards = cards.slice(1);
    const moveStartDelay = (fadedCards.length - 1) * fadeStagger + fadeDuration + movePause;
    const animations = [];
    let totalDuration = 0;

    for (const [index, card] of cards.entries()) {
        const fromX = card.style.getPropertyValue('--from-x');
        const fromY = card.style.getPropertyValue('--from-y');
        const startTransform = `translate(${fromX}, ${fromY})`;

        card.style.opacity = index === 0 ? '1' : '0';
        card.style.transform = startTransform;

        if (index > 0) {
            animations.push(
                card.animate(
                    [
                        { opacity: 0 },
                        { opacity: 1 },
                    ],
                    {
                        duration: fadeDuration,
                        delay: (index - 1) * fadeStagger,
                        easing: 'ease',
                        fill: 'forwards',
                    },
                ),
            );
        }

        const moveDelay = moveStartDelay + Math.max(index - 1, 0) * moveStagger;
        animations.push(
            card.animate(
                [
                    { transform: startTransform },
                    { transform: 'translate(0, 0)' },
                ],
                {
                    duration: moveDuration,
                    delay: moveDelay,
                    easing: moveEase,
                    fill: 'forwards',
                },
            ),
        );

        totalDuration = Math.max(totalDuration, moveDelay + moveDuration);
    }

    return { animations, totalDuration, moveStartDelay };
};

const showStartPositions = (cards) => {
    for (const card of cards) {
        const fromX = card.style.getPropertyValue('--from-x');
        const fromY = card.style.getPropertyValue('--from-y');

        card.style.opacity = '1';
        card.style.transform = `translate(${fromX}, ${fromY})`;
    }
};

const markAnimationComplete = (section) => {
    section.querySelector(placeholdersSelector)?.classList.add('is-animation-complete');
};

const playAnimation = (section) => {
    if (section.dataset.socialProofAnimatedPlayed === 'true') {
        return;
    }

    section.dataset.socialProofAnimatedPlayed = 'true';
    const cards = setCardStartPoints(section);

    if (lockAtStartPosition) {
        showStartPositions(cards);
        return;
    }

    if (typeof cards[0]?.animate !== 'function') {
        section.classList.add('is-animated');
        markAnimationComplete(section);
        return;
    }

    const { animations, moveStartDelay } = animateCards(cards);

    // Reveal the text shortly after the cards begin moving, so it fades in
    // during the placement movement rather than after the cards settle.
    const textRevealDelay = moveStartDelay + textRevealOffset;
    window.setTimeout(() => markAnimationComplete(section), textRevealDelay);

    Promise.allSettled(animations.map((animation) => animation.finished)).then(() => {
        section.classList.add('is-animated');
    });
};

const initSection = (section) => {
    if (section.dataset.socialProofAnimatedInitialized === 'true') {
        return;
    }

    section.dataset.socialProofAnimatedInitialized = 'true';

    if (window.matchMedia(staticQuery).matches) {
        section.classList.add('is-animated');
        markAnimationComplete(section);
        return;
    }

    if (!('IntersectionObserver' in window)) {
        playAnimation(section);
        return;
    }

    const observer = new IntersectionObserver(
        (entries) => {
            for (const entry of entries) {
                if (!entry.isIntersecting) {
                    continue;
                }

                playAnimation(section);
                observer.disconnect();
                break;
            }
        },
        {
            threshold: 0.25,
        },
    );

    observer.observe(section);
};

const initSocialProofAnimatedSections = (scope = document) => {
    const sections = scope.matches?.(sectionSelector)
        ? [scope]
        : scope.querySelectorAll(sectionSelector);

    for (const section of sections) {
        initSection(section);
    }
};

initSocialProofAnimatedSections();

document.addEventListener('shopify:section:load', (event) => {
    initSocialProofAnimatedSections(event.target);
});
